import type { Membership, RanchRef } from '../api/types'

/*
  Organization-wide memberships, expanded into one selectable entry per ranch.

  `GET /me` returns `ranch: null` for a membership that covers a whole organization
  (architecture §4; the seeded manager and owner rows are of this kind). The SPA works
  in a ranch scope: the switcher lists ranches, the API client stamps X-Ranch-Id, the
  outbox stamps the ranch at capture. Before this module an org-wide membership produced
  no scope at all: no switcher entry, no theme, 403s from every ranch route. Found
  2026-10-13 running the SPA against the seeded API as the dev manager.

  The ranches come from `GET /organizations/{orgId}/ranches`, which already answers with
  the ranches the caller may open. A ranch-level membership for the same ranch wins over
  the expanded one (it is the more specific grant). If the listing fails, the org-wide
  entry is kept unchanged so the user still sees the organization.
*/

export type ListRanches = (organizationId: string) => Promise<RanchRef[]>

export async function expandMemberships(
  memberships: Membership[],
  listRanches: ListRanches,
): Promise<Membership[]> {
  const out: Membership[] = []
  const seen = new Set<string>()
  const key = (organizationId: string, ranchId: string) => `${organizationId}/${ranchId}`

  // Ranch-level grants first, so they win over an expansion of the same ranch.
  for (const m of memberships) {
    if (!m.ranch) continue
    const k = key(m.organization.publicId, m.ranch.publicId)
    if (seen.has(k)) continue
    seen.add(k)
    out.push(m)
  }

  for (const m of memberships) {
    if (m.ranch) continue
    let ranches: RanchRef[]
    try {
      ranches = await listRanches(m.organization.publicId)
    } catch {
      out.push(m)
      continue
    }
    if (ranches.length === 0) {
      out.push(m)
      continue
    }
    for (const r of ranches) {
      const k = key(m.organization.publicId, r.publicId)
      if (seen.has(k)) continue
      seen.add(k)
      out.push({
        organization: m.organization,
        ranch: { publicId: r.publicId, name: r.name, timeZone: r.timeZone },
        role: m.role,
        organizationWide: true,
      })
    }
  }
  return out
}
